import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SofincoLogo from '../components/SofincoLogo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        toast({
          title: 'Connexion réussie',
          description: `Bienvenue ${result.user.firstName} !`,
        });

        // Redirect based on role
        setTimeout(() => {
          if (result.user.role === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/client/dashboard');
          }
        }, 500);
      } else {
        toast({
          title: 'Erreur de connexion',
          description: result.error,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur de connexion',
        description: 'Une erreur est survenue',
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  return (
  <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3E8FF' }}>
    <div className="flex-1 flex items-center justify-center py-8">
      <Card className="w-full max-w-md mx-4 p-8 bg-white shadow-xl rounded-2xl">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/okuo8oxo_IMG_0370.jpeg"
            alt="Younited"
            className="h-12 object-contain"
          />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Espace Client
          </h1>
          <p className="text-gray-500">
            Connectez-vous à votre compte
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-900 font-semibold">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-900 font-semibold">
              Mot de passe
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl border-gray-200 pr-12 focus:border-purple-500 focus:ring-purple-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-14 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
            style={{ backgroundColor: '#8B5CF6' }}
            disabled={loading}
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              Pas encore de compte ?{' '}
              <span className="text-purple-600 font-medium cursor-pointer hover:underline">
                Contactez-nous
              </span>
            </p>
          </div>
        </form>
      </Card>
    </div>

    {/* Footer Younited - Full Width */}
    <footer className="bg-black text-white w-full">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Younited Logo */}
        <div className="mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/q9qswhf3_YOUNITED.svg" 
            alt="Younited" 
            className="h-6 w-auto"
          />
        </div>

        {/* Legal text */}
        <div className="text-gray-400 text-xs leading-relaxed mb-6 space-y-4">
          <p>
            Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
          </p>
        </div>

        {/* Regulatory information */}
        <div className="text-gray-400 text-xs leading-relaxed mb-6 space-y-2">
          <p>
            Younited SA, établissement de crédit de droit français, agréé par l'Autorité de contrôle prudentiel et de résolution (ACPR) sous le n° 16488.
          </p>
          <p>
            Les emprunteurs sont couverts par une assurance Décès, Incapacité de travail, Invalidité Permanente Totale (DIT) souscrite auprès de MetLife Europe d.a.c. – MetLife Europe Insurance d.a.c. sont des filiales de MetLife, Inc. MetLife Europe d.a.c. est agréée et réglementée par la Central Bank of Ireland.
          </p>
          <p>
            Younited (France) S.A. est enregistrée sur le registre unique géré par l'ORIAS sous le n° 11061269 en qualité de courtier en assurances.
          </p>
          <p>
            Taux Annuel Effectif Global (TAEG) fixe à partir de 1,20 %, pour une durée comprise entre 6 et 180 mois, exclusivement dédié au financement de projets de rénovation énergétique ou au rachat de crédits liés à des travaux énergétiques, valable uniquement pour l'année 2026.
          </p>
        </div>

        {/* Company info */}
        <div className="text-gray-400 text-xs leading-relaxed mb-8">
          <p>
            Younited (France) SA au capital de 3 396 476€ – Siège : 21 rue de Châteaudun — 75009 PARIS – RCS Paris : 517 586 376
          </p>
        </div>

        {/* B Corp Logo and Social */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-6 border-t border-gray-800">
          {/* B Corp certified */}
          <div className="flex items-center space-x-3 mb-4 md:mb-0">
            <div className="bg-white rounded-full p-2">
              <svg className="w-6 h-6 text-black" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm0-8h-2V7h2v2zm4 8h-2v-4h2v4zm0-6h-2V9h2v2z"/>
              </svg>
            </div>
            <span className="text-white text-xs font-medium">Entreprise B Certifiée</span>
          </div>

          {/* Country flags */}
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <span className="text-lg">🇵🇹</span>
            <span className="text-lg">🇫🇷</span>
            <span className="text-lg">🇮🇹</span>
            <span className="text-lg">🇪🇸</span>
            <span className="text-lg">🇩🇪</span>
          </div>

          {/* Social media icons */}
          <div className="flex items-center space-x-4">
            <a href="#" className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-6 pt-4 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-xs">younited-credit.com — Privée</p>
        </div>
      </div>
    </footer>
  </div>
  );
};

export default Login;
